import { Component } from '@theme/component';

/**
 * Formats RTE content for styling and cleans Word/MSO paste markup.
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#cleanWordMarkup();
    this.querySelectorAll('table').forEach((table) => this.#formatTable(table));
  }

  /**
   * Normalize Microsoft Word / MSO richtext into clean semantic HTML.
   */
  #cleanWordMarkup() {
    if (!this.querySelector('[class*="Mso"], [style*="mso-"], [style*="Century Gothic"]')) return;

    // Remove empty paragraphs / leftover Word comments
    this.querySelectorAll('p').forEach((p) => {
      const text = (p.textContent || '').replace(/\u00a0/g, ' ').trim();
      if (!text) p.remove();
    });

    // Convert fake Word bullet paragraphs into real lists
    this.#convertMsoLists();

    // Unwrap useless spans and strip Word inline styles/classes
    this.querySelectorAll('span').forEach((span) => {
      span.removeAttribute('lang');
      span.removeAttribute('style');
      span.removeAttribute('class');
      if (!span.attributes.length) {
        const parent = span.parentNode;
        if (!parent) return;
        while (span.firstChild) parent.insertBefore(span.firstChild, span);
        parent.removeChild(span);
      }
    });

    this.querySelectorAll('*').forEach((el) => {
      if (el === this) return;

      const className = el.getAttribute('class') || '';
      if (/Mso/i.test(className)) el.removeAttribute('class');

      const style = el.getAttribute('style') || '';
      if (style) {
        // Keep only useful alignment on headings/paragraphs if needed — drop Word junk
        el.removeAttribute('style');
        el.removeAttribute('align');
      }

      el.removeAttribute('lang');
    });

    // Drop duplicate policy title inside body (page already has an H1)
    const pageTitle = document.querySelector('.customer-care__title');
    if (pageTitle) {
      const titleText = pageTitle.textContent.trim().toLowerCase();
      this.querySelectorAll('h1, h2, h3').forEach((heading) => {
        const text = heading.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
        if (text === titleText || text === 'privacy policy' || text === 'terms & conditions' || text === 'terms and conditions') {
          heading.remove();
        }
      });
    }

    // Normalize "Last updated..." as a muted eyebrow
    const first = this.querySelector('h5, h4, p');
    if (first && /last updated/i.test(first.textContent || '')) {
      const meta = document.createElement('p');
      meta.className = 'customer-care__meta';
      meta.textContent = first.textContent.replace(/\s+/g, ' ').trim();
      first.replaceWith(meta);
    }

    // Drop standalone "nesset" brand line under title
    this.querySelectorAll('p').forEach((p) => {
      const text = p.textContent.replace(/\s+/g, ' ').trim();
      if (/^nesset$/i.test(text)) p.remove();
    });
  }

  /**
   * Turn consecutive MsoListBullet paragraphs into <ul><li>
   */
  #convertMsoLists() {
    const nodes = Array.from(this.childNodes);
    let i = 0;

    while (i < nodes.length) {
      const node = nodes[i];
      if (!(node instanceof HTMLElement)) {
        i += 1;
        continue;
      }

      const isBullet =
        /MsoListBullet/i.test(node.className || '') ||
        (/^p$/i.test(node.tagName) && /^[·•▪◦-]\s*/.test((node.textContent || '').trim()));

      if (!isBullet) {
        i += 1;
        continue;
      }

      const items = [];
      let j = i;
      while (j < nodes.length) {
        const current = nodes[j];
        if (!(current instanceof HTMLElement)) break;
        const currentIsBullet =
          /MsoListBullet/i.test(current.className || '') ||
          (/^p$/i.test(current.tagName) && /^[·•▪◦-]\s*/.test((current.textContent || '').trim()));
        if (!currentIsBullet) break;
        items.push(current);
        j += 1;
      }

      const ul = document.createElement('ul');
      items.forEach((item) => {
        const li = document.createElement('li');
        let html = item.innerHTML
          .replace(/<!--\[if !supportLists\]-->[\s\S]*?<!--\[endif\]-->/gi, '')
          .replace(/^[·•▪◦]\s*/u, '')
          .replace(/^<span[^>]*>[·•▪◦]\s*<\/span>/i, '');
        // Strip leading Word bullet glyph text nodes
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const text = (tmp.textContent || '').replace(/^[·•▪◦\s]+/u, '').trim();
        li.textContent = text;
        ul.appendChild(li);
        item.remove();
      });

      const next = nodes[j] || null;
      if (next) this.insertBefore(ul, next);
      else this.appendChild(ul);

      nodes.splice(i, items.length, ul);
      i += 1;
    }
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    if (table.parentElement?.classList.contains('rte-table-wrapper')) return;

    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }

    // Unwrap centered Word table wrappers
    const center = wrapper.parentElement;
    if (center && center.tagName === 'DIV' && center.getAttribute('align') === 'center') {
      center.replaceWith(wrapper);
    }
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
